import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { baseUrl } from "../shared";
import '../pages/App.css';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';

const authHeaders = () => ({
  'username': localStorage.getItem('username'),
  'bToken': localStorage.getItem('bToken'),
});

export function EditCompanies({ omnixAdminMode = false, onClose }) {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [childRelations, setChildRelations] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [description, setDescription] = useState('');
  const [logoFilename, setLogoFilename] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoBlobUrl, setLogoBlobUrl] = useState(null);
  const [hexColour, setHexColour] = useState('');
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  // Omnix link state
  const canUseOmnix = localStorage.getItem('canUseOmnixIntegration') === '1';
  const [omnixLink, setOmnixLink] = useState(null);       // current link or null
  const [omnixLinkLoading, setOmnixLinkLoading] = useState(false);
  const [omnixCustomerOptions, setOmnixCustomerOptions] = useState([]);
  const [omnixCustomerInput, setOmnixCustomerInput] = useState('');
  const [selectedOmnixCustomer, setSelectedOmnixCustomer] = useState(null);
  const [omnixLinkMsg, setOmnixLinkMsg] = useState('');
  const omnixSearchTimer = useRef(null);

  useEffect(() => {
    fetch(baseUrl + 'company/list', { method: 'GET', headers: authHeaders() })
      .then(r => r.json())
      .then(data => setCompanies(Array.isArray(data) ? data : []))
      .catch(err => console.error('company list error', err));

    fetch(baseUrl + 'company/allChildren', { method: 'GET', headers: authHeaders() })
      .then(r => r.json())
      .then(data => setChildRelations(Array.isArray(data) ? data : []))
      .catch(err => console.error('allChildren error', err));
  }, []);

  const handleSelectCompany = async (companyCode) => {
    if (!companyCode) return;
    const co = companies.find(c => c.company === companyCode);
    if (!co) return;
    setSelectedCompany(companyCode);
    setDescription(co.description || '');
    setHexColour(co.hex_colour || '');
    setLogoFilename(co.logo || '');
    setLogoFile(null);
    setLogoPreview(null);
    setOmnixLink(null);
    setSelectedOmnixCustomer(null);
    setOmnixLinkMsg('');
    if (logoBlobUrl) { URL.revokeObjectURL(logoBlobUrl); setLogoBlobUrl(null); }
    setSelectedChildren(
      childRelations.filter(r => r.parentCompany === companyCode).map(r => r.childCompany)
    );
    if (co.logo) {
      try {
        const res = await fetch(baseUrl + 'company/logo/' + encodeURIComponent(co.logo), { headers: authHeaders() });
        if (res.ok) {
          const blob = await res.blob();
          setLogoBlobUrl(URL.createObjectURL(blob));
        }
      } catch (err) {
        console.error('Logo fetch error', err);
      }
    }
    if (canUseOmnix) {
      setOmnixLinkLoading(true);
      fetch(baseUrl + 'omnix/company-links/by-company/' + encodeURIComponent(companyCode), { headers: authHeaders() })
        .then(r => r.json())
        .then(data => setOmnixLink(data))
        .catch(() => setOmnixLink(null))
        .finally(() => setOmnixLinkLoading(false));
    }
  };

  const handleOmnixCustomerSearch = (value) => {
    setOmnixCustomerInput(value);
    if (omnixSearchTimer.current) clearTimeout(omnixSearchTimer.current);
    if (!value) { setOmnixCustomerOptions([]); return; }
    omnixSearchTimer.current = setTimeout(() => {
      fetch(baseUrl + 'omnix/customers?q=' + encodeURIComponent(value), { headers: authHeaders() })
        .then(r => r.json())
        .then(data => setOmnixCustomerOptions(Array.isArray(data) ? data : []))
        .catch(() => setOmnixCustomerOptions([]));
    }, 300);
  };

  const handleCreateOmnixLink = async () => {
    if (!selectedOmnixCustomer || !selectedCompany) return;
    setOmnixLinkMsg('');
    const res = await fetch(baseUrl + 'omnix/company-links', {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        aimsCompanyCode: selectedCompany,
        omnixCustomerId: selectedOmnixCustomer.id,
        omnixCustomerName: selectedOmnixCustomer.name,
      }),
    });
    const data = await res.json();
    if (res.ok) {
      setOmnixLink(data);
      setSelectedOmnixCustomer(null);
      setOmnixCustomerInput('');
      setOmnixLinkMsg('Link created successfully.');
    } else {
      setOmnixLinkMsg(data.message || 'Failed to create link.');
    }
  };

  const handleDeleteOmnixLink = async () => {
    if (!omnixLink) return;
    setOmnixLinkMsg('');
    const res = await fetch(baseUrl + 'omnix/company-links/' + omnixLink.id, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (res.ok) {
      setOmnixLink(null);
      setOmnixLinkMsg('Link removed.');
    } else {
      setOmnixLinkMsg('Failed to remove link.');
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0] || null;
    setLogoFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setLogoPreview(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let finalLogo = logoFilename;

      if (logoFile) {
        const formData = new FormData();
        formData.append('logo', logoFile);
        formData.append('company', selectedCompany);
        const uploadRes = await fetch(baseUrl + 'company/uploadLogo', {
          method: 'POST',
          headers: authHeaders(),
          body: formData,
        });
        const uploadData = await uploadRes.json();
        if (uploadData.filename) finalLogo = uploadData.filename;
      }

      await fetch(baseUrl + 'company/update', {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ company: selectedCompany, description, logo: finalLogo, hex_colour: hexColour || null }),
      });

      await fetch(baseUrl + 'company/setChildren', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ parentCompany: selectedCompany, children: selectedChildren }),
      });

      setLogoFilename(finalLogo);
      setLogoFile(null);
      setLogoPreview(null);
      if (logoBlobUrl) { URL.revokeObjectURL(logoBlobUrl); setLogoBlobUrl(null); }
      try {
        const res = await fetch(baseUrl + 'company/logo/' + encodeURIComponent(finalLogo), { headers: authHeaders() });
        if (res.ok) setLogoBlobUrl(URL.createObjectURL(await res.blob()));
      } catch (err) { console.error('Logo fetch error', err); }
      setCompanies(prev => prev.map(c => c.company === selectedCompany ? { ...c, description, logo: finalLogo, hex_colour: hexColour || null } : c));
      setChildRelations(prev => [
        ...prev.filter(r => r.parentCompany !== selectedCompany),
        ...selectedChildren.map(child => ({ parentCompany: selectedCompany, childCompany: child })),
      ]);
      setMessage('Company updated successfully.');
      setDialogOpen(true);
    } catch (err) {
      console.error(err);
      setMessage('Error saving company: ' + err.message);
      setDialogOpen(true);
    }
    setSaving(false);
  };

  const childOptions = companies.filter(c => c.company !== selectedCompany);

  return (
    <div className="admin">
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle />
        <DialogContent>
          <DialogContentText>{message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--text)' }}>Edit Companies</Typography>
        {onClose && (
          <Button startIcon={<ArrowBackIcon />} variant="outlined" color="inherit" sx={{ color: 'var(--muted)', borderColor: 'var(--border-strong)' }} onClick={onClose}>
            Back to Admin
          </Button>
        )}
      </Box>

      <FormControl fullWidth margin="dense">
        <InputLabel shrink htmlFor="companySelect">Company</InputLabel>
        <Select
          native
          value={selectedCompany}
          inputProps={{ id: 'companySelect' }}
          label="Company"
          fullWidth
          onChange={(e) => handleSelectCompany(e.target.value)}
        >
          <option value="">Select a company to edit</option>
          {companies.map(c => (
            <option value={c.company} key={c.company}>{c.description} ({c.company})</option>
          ))}
        </Select>
      </FormControl>

      {selectedCompany && (
        <>
          <Box>
            <TextField
              fullWidth
              margin="dense"
              label="Display Name"
              variant="outlined"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Box>

          <Box sx={{ mt: 1.5, mb: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Logo</Typography>
            {(logoPreview || logoBlobUrl) && (
              <Box sx={{ mb: 1, position: 'relative', display: 'inline-block' }}>
                <img
                  src={logoPreview || logoBlobUrl}
                  alt="Company logo"
                  style={{ maxHeight: 80, maxWidth: 200, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 4, padding: 4, display: 'block' }}
                />
                <Box
                  onClick={() => {
                    setLogoFile(null);
                    setLogoPreview(null);
                    setLogoFilename('');
                    if (logoBlobUrl) { URL.revokeObjectURL(logoBlobUrl); setLogoBlobUrl(null); }
                  }}
                  sx={{
                    position: 'absolute', top: -8, right: -8,
                    width: 20, height: 20, borderRadius: '50%',
                    backgroundColor: 'var(--fail)', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 'bold', lineHeight: 1,
                    cursor: 'pointer', userSelect: 'none',
                    '&:hover': { opacity: 0.85 },
                  }}
                >×</Box>
              </Box>
            )}
            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
              {logoFilename ? `Current file: ${logoFilename}` : 'No logo set'}
            </Typography>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoFileChange}
            />
          </Box>

          <Box sx={{ mt: 1.5, mb: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Calendar Colour</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 40, height: 40, borderRadius: 1, border: '1px solid var(--border)',
                  bgcolor: hexColour || '#cccccc', flexShrink: 0, position: 'relative', overflow: 'hidden',
                }}
              >
                <input
                  type="color"
                  value={hexColour || '#cccccc'}
                  onChange={e => setHexColour(e.target.value)}
                  style={{
                    position: 'absolute', top: 0, left: 0,
                    width: '200%', height: '200%',
                    opacity: 0, cursor: 'pointer', border: 'none',
                  }}
                />
              </Box>
              <TextField
                size="small"
                label="Hex value"
                value={hexColour}
                onChange={e => {
                  const val = e.target.value;
                  if (/^#?[0-9A-Fa-f]{0,6}$/.test(val)) {
                    setHexColour(val.startsWith('#') ? val : val ? '#' + val : '');
                  }
                }}
                inputProps={{ maxLength: 7, style: { fontFamily: 'monospace' } }}
                sx={{ width: 140 }}
                placeholder="#000000"
              />
              {hexColour && (
                <Button size="small" variant="text" onClick={() => setHexColour('')}>Clear</Button>
              )}
            </Box>
          </Box>

          <FormControl fullWidth margin="dense">
            <InputLabel>Child Companies</InputLabel>
            <Select
              multiple
              value={selectedChildren}
              onChange={(e) => setSelectedChildren(e.target.value)}
              label="Child Companies"
              renderValue={(selected) => selected.length === 0 ? 'None' : selected.join(', ')}
            >
              {childOptions.map(c => (
                <MenuItem key={c.company} value={c.company}>
                  <Checkbox checked={selectedChildren.includes(c.company)} />
                  <ListItemText primary={`${c.description} (${c.company})`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {canUseOmnix && (
            <Box sx={{ mt: 2, p: 1.5, border: '1px solid var(--border)', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Omnix Integration Link</Typography>
              {omnixLinkLoading ? (
                <CircularProgress size={20} />
              ) : omnixLink ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={`Linked → ${omnixLink.omnixCustomerName || 'Customer #' + omnixLink.omnixCustomerId}`} color="success" size="small" />
                  <Button size="small" color="error" variant="text" onClick={handleDeleteOmnixLink}>Remove Link</Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">Not linked to an Omnix customer.</Typography>
                  <Autocomplete
                    options={omnixCustomerOptions}
                    getOptionLabel={(o) => `${o.name}${o.customerCode ? ` (${o.customerCode})` : ''}`}
                    value={selectedOmnixCustomer}
                    onChange={(_, val) => setSelectedOmnixCustomer(val)}
                    onInputChange={(_, v) => handleOmnixCustomerSearch(v)}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Search Omnix customer" size="small" placeholder="Type customer name…" />
                    )}
                    noOptionsText={omnixCustomerInput ? 'No customers found' : 'Type to search…'}
                    sx={{ maxWidth: 360 }}
                  />
                  <Box>
                    <Button size="small" variant="outlined" onClick={handleCreateOmnixLink} disabled={!selectedOmnixCustomer}>
                      Link Company
                    </Button>
                  </Box>
                </Box>
              )}
              {omnixLinkMsg && (
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: omnixLinkMsg.includes('success') || omnixLinkMsg.includes('created') ? 'success.main' : 'error.main' }}>
                  {omnixLinkMsg}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ mr: 1 }}>
              Save Changes
            </Button>
          </Box>
        </>
      )}

      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={() => navigate(0)}>Cancel</Button>
      </Box>
    </div>
  );
}

export default EditCompanies;
